import Debug from 'debug'
import { create } from 'zustand'
import { apiClient } from '@/services/api-client'
import { useAuthStore } from '@/stores/auth-store'
import { clearVisibilityCache, getVisibleFields, isFieldVisible, isSectionVisible } from '@/services/rules-engine'
import { transformAnswersForSubmission, transformAnswersFromWire } from '@/services/value-transformers'
import type {
  ActionLayoutSection,
  ContentLayoutSection,
  DataSubmissionResult,
  FieldsLayoutSection,
  FormLayout,
  FormLayoutField,
  FormLayoutFieldRecordType,
  FormLayoutRow,
  FormLayoutSection,
  PolicyLayoutSection,
  RedirectLayoutSection,
  RelatedFormLayout,
  RelatedLayoutSection,
} from '@/types/api'

const log = Debug('gg:form')

// ─── Types ──────────────────────────────────────────────────────────────────

export interface FormStep {
  id: string
  heading: string
  fields: FormLayoutField[]
  visible: boolean
}

export interface ContentBlock {
  key: string
  classes: string
  text: string
}

export interface PolicyBlock {
  key: string
  classes: string
  text: string
}

export interface RedirectRule {
  action: string
  url: string
}

export interface ActionButton {
  action: 'save' | 'next' | 'back' | 'reset' | 'add' | 'remove'
  label: string
}

export interface RelatedLayoutConfig {
  objectName: string
  field: string
  layout: FormLayout
  allFields: FormLayoutField[]
  requiredInstanceCount: number | string
  initialInstanceCount: number | string
  maxInstanceCount: number | string
}

export type RelatedData = Record<string, Record<string, unknown>[]>
export type DeletedData = Record<string, string[]>

export interface FormStoreState {
  // Layout
  layout: FormLayout | null
  form: string | null
  layoutName: string | null
  instanceId: string | null
  allFields: FormLayoutField[]
  steps: FormStep[]
  isMultiStep: boolean
  recordTypeId: string | null

  // Section flags
  allowAnonymous: boolean
  autoSave: boolean
  redirects: RedirectRule[]
  showTableOfContents: boolean
  actionButtons: ActionButton[]

  // Content/policy sections
  contentBlocks: ContentBlock[]
  policyBlocks: PolicyBlock[]

  // Related layouts (child objects)
  relatedLayouts: RelatedLayoutConfig[]

  // Navigation
  currentStepIndex: number

  // Data
  answers: Record<string, unknown>
  errors: Record<string, string[]>
  touchedFields: Record<string, boolean>

  // Status
  isLoading: boolean
  isSaving: boolean
  isSubmitting: boolean
  isComplete: boolean
  isDeclined: boolean
  declineReasons: string[]
  loadError: string | null
  saveError: string | null
  dataHasChanged: boolean

  // Auto-save timer
  _autoSaveTimer: ReturnType<typeof setInterval> | null

  // Actions
  loadForm: (form: string, layoutName: string, instanceId?: string) => Promise<void>
  updateAnswer: (fieldName: string, value: unknown) => void
  bulkUpdateAnswers: (values: Record<string, unknown>) => void
  markFieldTouched: (fieldName: string) => void
  validateField: (fieldName: string) => string[]
  validateCurrentStep: () => boolean
  validateAll: () => boolean
  goToStep: (index: number) => void
  goToNextStep: () => void
  goToPreviousStep: () => void
  submitForm: (done?: boolean) => Promise<boolean>
  saveProgress: () => Promise<boolean>
  tryRedirect: (status: string, serverRedirectUrl?: string) => boolean
  reset: () => void
  stopAutoSave: () => void

  // Related layout actions
  getRelatedData: (objectName: string) => Record<string, unknown>[]
  addRelatedRow: (objectName: string) => void
  removeRelatedRow: (objectName: string, index: number) => void
  updateRelatedAnswer: (objectName: string, rowIndex: number, fieldName: string, value: unknown) => void
  resolveRelatedInstanceCount: (value: number | string) => number

  // Computed helpers
  getVisibleSteps: () => FormStep[]
  getCurrentStep: () => FormStep | null
  getVisibleFieldsForStep: (stepIndex: number) => FormLayoutField[]
  getProgress: () => { current: number; total: number; percent: number }
  resolveField: (field: FormLayoutField) => FormLayoutField
}

function extractFieldsFromSection(section: FieldsLayoutSection): FormLayoutField[] {
  return section.rows.flatMap((row: FormLayoutRow) => row.fields.filter(f => f.type !== 'empty'))
}

function buildRelatedLayoutConfigs(layout: FormLayout): RelatedLayoutConfig[] {
  if (!layout.relatedLayouts) return []
  return layout.relatedLayouts.map((rl: RelatedFormLayout) => ({
    objectName: rl.objectName,
    field: rl.field,
    layout: rl.layout,
    allFields: rl.layout.sections
      .filter((s): s is FieldsLayoutSection => s.type === 'fields')
      .flatMap(s => extractFieldsFromSection(s)),
    requiredInstanceCount: rl.requiredInstanceCount ?? 0,
    initialInstanceCount: rl.initialInstanceCount ?? 0,
    maxInstanceCount: rl.maxInstanceCount ?? 0,
  }))
}

let nextRefCounter = 10000
function generateRef(): string {
  return `#ref-${nextRefCounter++}`
}

function resolveInstanceCount(value: number | string, answers: Record<string, unknown>): number {
  if (typeof value === 'number') return value
  if (typeof value === 'string') {
    if (value.startsWith('@')) {
      const fieldName = value.substring(1)
      const fieldValue = answers[fieldName]
      if (typeof fieldValue === 'number') return fieldValue
      if (typeof fieldValue === 'string') {
        const parsed = Number.parseInt(fieldValue, 10)
        return Number.isNaN(parsed) ? 0 : parsed
      }
    }
    const parsed = Number.parseInt(value, 10)
    return Number.isNaN(parsed) ? 0 : parsed
  }
  return 0
}

function parseSectionFlags(sections: FormLayoutSection[]) {
  let allowAnonymous = false
  let autoSave = true // defaults to true per Aurelia behavior
  let showTableOfContents = false
  let recordTypeId: string | null = null
  const redirects: RedirectRule[] = []
  const contentBlocks: ContentBlock[] = []
  const policyBlocks: PolicyBlock[] = []
  const actionButtons: ActionButton[] = []

  for (const section of sections) {
    switch (section.type) {
      case 'allowAnonymous':
        allowAnonymous = (section.specialLayout as { allowAnonymous?: boolean })?.allowAnonymous === true
        break
      case 'autoSave':
        autoSave = (section.specialLayout as { autoSave?: boolean })?.autoSave !== false
        break
      case 'redirect': {
        const s = section as RedirectLayoutSection
        if (s.specialLayout?.url) {
          redirects.push({ action: s.specialLayout.action, url: s.specialLayout.url })
        }
        break
      }
      case 'tableOfContents':
        showTableOfContents = (section.specialLayout as { show?: boolean })?.show === true
        break
      case 'recordType':
        recordTypeId = (section.specialLayout as { recordTypeId?: string })?.recordTypeId || null
        break
      case 'content': {
        const s = section as ContentLayoutSection
        if (s.specialLayout) {
          contentBlocks.push({
            key: s.specialLayout.key,
            classes: s.specialLayout.classes,
            text: s.specialLayout.text,
          })
        }
        break
      }
      case 'policy': {
        const s = section as PolicyLayoutSection
        if (s.specialLayout) {
          policyBlocks.push({
            key: s.specialLayout.key,
            classes: s.specialLayout.classes,
            text: s.specialLayout.text,
          })
        }
        break
      }
      case 'action': {
        const s = section as ActionLayoutSection
        if (s.specialLayout) {
          actionButtons.push({ action: s.specialLayout.action, label: s.specialLayout.label })
        }
        break
      }
    }
  }

  return {
    allowAnonymous,
    autoSave,
    redirects,
    showTableOfContents,
    recordTypeId,
    contentBlocks,
    policyBlocks,
    actionButtons,
  }
}

function buildSteps(sections: FormLayoutSection[]): FormStep[] {
  return sections
    .filter((s): s is FieldsLayoutSection => s.type === 'fields')
    .map((section, i) => {
      const rawHeading = section.heading || `Section ${i + 1}`
      // Labels starting with '!' are suppressed (Aurelia convention)
      const heading = rawHeading.startsWith('!') ? '' : rawHeading
      return {
        id: `step-${i}`,
        heading,
        fields: extractFieldsFromSection(section),
        visible: true,
      }
    })
}

/**
 * Resolve a field's properties with byRecordType overrides applied.
 * If there's a recordTypeId and the field has overrides for it, merge them.
 */
function resolveFieldWithRecordType(field: FormLayoutField, recordTypeId: string | null): FormLayoutField {
  if (!recordTypeId || !field.byRecordType) return field
  const override = field.byRecordType[recordTypeId]
  if (!override) return field

  // Merge override properties onto the field (override takes precedence)
  return {
    ...field,
    ...stripUndefined(override),
    name: field.name,
    originalType: field.originalType,
  } as FormLayoutField
}

function stripUndefined(obj: FormLayoutFieldRecordType): Partial<FormLayoutFieldRecordType> {
  const result: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined) result[key] = value
  }
  return result as Partial<FormLayoutFieldRecordType>
}

function calculateAge(birthDate: string): number | null {
  const d = new Date(birthDate)
  if (Number.isNaN(d.getTime())) return null
  const today = new Date()
  let age = today.getFullYear() - d.getFullYear()
  const monthDiff = today.getMonth() - d.getMonth()
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < d.getDate())) {
    age--
  }
  return age
}

function validateFieldValue(field: FormLayoutField, value: unknown): string[] {
  const errors: string[] = []

  // Required check - with special handling for checkboxes and multiselect
  if (field.isRequired) {
    if (field.type === 'checkbox' || field.type === 'boolean') {
      if (!value || (Array.isArray(value) && value.length === 0)) {
        errors.push(`${field.label || field.name} is required`)
      }
    } else if (
      field.type === 'multi-select' ||
      field.type === 'multiselect-picklist' ||
      field.type === 'multipicklist'
    ) {
      if (!value || (Array.isArray(value) && value.length === 0)) {
        errors.push(`${field.label || field.name} is required`)
      }
    } else if (value === undefined || value === null || value === '') {
      errors.push(`${field.label || field.name} is required`)
    }
  }

  if (value && typeof value === 'string') {
    if (field.type === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      errors.push('Please enter a valid email address')
    }
    if (field.type === 'phone' && value.replace(/\D/g, '').length < 10) {
      errors.push('Please enter a valid phone number')
    }
    if (field.maxLength && value.length > field.maxLength) {
      errors.push(`Maximum ${field.maxLength} characters allowed`)
    }
  }

  if (field.validation) {
    // Numeric min/max
    if (field.validation.min !== undefined && typeof value === 'number' && value < field.validation.min) {
      errors.push(`Minimum value is ${field.validation.min}`)
    }
    if (field.validation.max !== undefined && typeof value === 'number' && value > field.validation.max) {
      errors.push(`Maximum value is ${field.validation.max}`)
    }

    // Pattern/regex validation
    if (field.validation.pattern && typeof value === 'string' && value) {
      try {
        const regex = new RegExp(field.validation.pattern)
        if (!regex.test(value)) {
          errors.push('Please enter a valid value')
        }
      } catch {
        // Invalid regex pattern from API - skip
      }
    }

    // Age validation for date fields
    if (field.validation.age && typeof value === 'string' && value) {
      const age = calculateAge(value)
      if (age !== null) {
        const { min, max, gt, lt, eq } = field.validation.age
        if (typeof min === 'number' && age < min) errors.push(`Must be at least ${min} years old`)
        if (typeof max === 'number' && age > max) errors.push(`Must be at most ${max} years old`)
        if (typeof gt === 'number' && age <= gt) errors.push(`Must be over ${gt} years old`)
        if (typeof lt === 'number' && age >= lt) errors.push(`Must be under ${lt} years old`)
        if (typeof eq === 'number' && age !== eq) errors.push(`Must be exactly ${eq} years old`)
      }
    }
  }

  return errors
}

// ─── Tracking data (gclid/utm/fbclid) ────────────────────────────────────

const GCLID_KEY = 'app:gclid'
const FBCLID_KEY = 'app:fbclid'
const TRACKING_KEY = 'app:tracking'

function safeSessionGet(key: string): string | null {
  try { return sessionStorage.getItem(key) } catch { return null }
}

function safeSessionSet(key: string, value: string) {
  try { sessionStorage.setItem(key, value) } catch { /* noop */ }
}

// Call on app boot to capture URL params + cookies into sessionStorage
export function captureTrackingData() {
  try {
    const params = new URLSearchParams(window.location.search)

    const gclid = params.get('gclid')
    if (gclid) safeSessionSet(GCLID_KEY, gclid)

    const fbclid = params.get('fbclid')
    if (fbclid) safeSessionSet(FBCLID_KEY, fbclid)

    // Capture all utm_* params
    const utm: Record<string, string> = {}
    for (const [key, value] of params.entries()) {
      if (key.toLowerCase().startsWith('utm_')) {
        utm[key.toLowerCase()] = value
      }
    }

    // Fall back to cookies for any missing standard UTM params
    const standardUtm = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content']
    for (const key of standardUtm) {
      if (!(key in utm)) {
        const cookieValue = document.cookie
          .split('; ')
          .find(row => row.startsWith(`${key}=`))
          ?.split('=')[1]
        if (cookieValue) {
          utm[key] = decodeURIComponent(cookieValue)
        }
      }
    }

    if (Object.keys(utm).length > 0) {
      safeSessionSet(TRACKING_KEY, JSON.stringify(utm))
    }

    log('tracking captured: gclid=%s fbclid=%s utm=%o', gclid, fbclid, utm)
  } catch {
    // Tracking is non-critical
  }
}

// Returns tracking data in the Aurelia wire format: utm as JSON string
function getTrackingData(): { gclid?: string; fbclid?: string; utm?: string } {
  const result: { gclid?: string; fbclid?: string; utm?: string } = {}
  try {
    const gclid = safeSessionGet(GCLID_KEY)
    if (gclid) result.gclid = gclid

    const fbclid = safeSessionGet(FBCLID_KEY)
    if (fbclid) result.fbclid = fbclid

    const utm = safeSessionGet(TRACKING_KEY)
    if (utm) result.utm = utm // Already a JSON string
  } catch {
    // Storage not available
  }
  return result
}

// ─── Auto-save interval ───────────────────────────────────────────────────

const AUTO_SAVE_INTERVAL_MS = 10_000

// ─── Initial state ────────────────────────────────────────────────────────

const initialState = {
  layout: null as FormLayout | null,
  form: null as string | null,
  layoutName: null as string | null,
  instanceId: null as string | null,
  allFields: [] as FormLayoutField[],
  steps: [] as FormStep[],
  isMultiStep: false,
  recordTypeId: null as string | null,
  allowAnonymous: false,
  autoSave: true,
  redirects: [] as RedirectRule[],
  showTableOfContents: false,
  actionButtons: [] as ActionButton[],
  contentBlocks: [] as ContentBlock[],
  policyBlocks: [] as PolicyBlock[],
  relatedLayouts: [] as RelatedLayoutConfig[],
  currentStepIndex: 0,
  answers: {} as Record<string, unknown>,
  errors: {} as Record<string, string[]>,
  touchedFields: {} as Record<string, boolean>,
  isLoading: false,
  isSaving: false,
  isSubmitting: false,
  isComplete: false,
  isDeclined: false,
  declineReasons: [] as string[],
  loadError: null as string | null,
  saveError: null as string | null,
  dataHasChanged: false,
  _autoSaveTimer: null as ReturnType<typeof setInterval> | null,
}

export const useFormStore = create<FormStoreState>((set, get) => ({
  ...initialState,

  async loadForm(form: string, layoutName: string, instanceId?: string) {
    set({ isLoading: true, loadError: null, form, layoutName, instanceId })
    try {
      const { data: layout } = await apiClient.get<FormLayout>(`/layout/${form}/${layoutName}`)

      const steps = buildSteps(layout.sections)
      const allFields = steps.flatMap(s => s.fields)
      const flags = parseSectionFlags(layout.sections)
      const isMultiStep = steps.length > 1

      log('loaded layout: %s/%s (%d steps, %d fields)', form, layoutName, steps.length, allFields.length)

      // Auth check: if form requires auth and user isn't authenticated, redirect to login
      if (!flags.allowAnonymous && !useAuthStore.getState().isAuthenticated) {
        log('form requires auth, redirecting to login')
        set({ isLoading: false })
        window.location.href = `/login?redirect=${encodeURIComponent(window.location.pathname)}`
        return
      }

      // Anonymous forms: skip data loading even if instanceId was provided
      const shouldLoadData = instanceId && !flags.allowAnonymous

      // Build field type map for fromWire conversion
      const fieldTypes: Record<string, { type: string; originalType: string }> = {}
      for (const field of allFields) {
        const resolved = resolveFieldWithRecordType(field, flags.recordTypeId)
        fieldTypes[resolved.name] = { type: resolved.type, originalType: resolved.originalType }
      }

      // Load existing data if there's an instance (skip for anonymous forms)
      let answers: Record<string, unknown> = {}
      if (shouldLoadData) {
        try {
          const { data } = await apiClient.get<Record<string, unknown>>(`/data/${form}/${layoutName}/${instanceId}`)
          // Convert from wire format to display format
          answers = data ? transformAnswersFromWire(data, fieldTypes) : {}
          log('loaded %d existing answers', Object.keys(answers).length)
        } catch (err) {
          log('no existing data found (or error): %o', err)
        }
      }

      // Initialize checkbox/boolean defaults from field config
      for (const field of allFields) {
        const resolved = resolveFieldWithRecordType(field, flags.recordTypeId)
        if (
          (resolved.type === 'checkbox' || resolved.type === 'boolean') &&
          resolved.quickValue !== undefined &&
          answers[resolved.name] === undefined
        ) {
          answers[resolved.name] = resolved.quickValue
        }
      }

      // Build related layout configs
      const relatedLayouts = buildRelatedLayoutConfigs(layout)

      // Initialize related data with initial/required instance counts
      if (relatedLayouts.length > 0) {
        const related = (answers.__related as Record<string, Record<string, unknown>[]>) || {}
        for (const rl of relatedLayouts) {
          const rows = related[rl.objectName] || []
          const initialCount =
            resolveInstanceCount(rl.initialInstanceCount, answers) ||
            resolveInstanceCount(rl.requiredInstanceCount, answers)
          while (initialCount && rows.length < initialCount) {
            rows.push({})
          }
          related[rl.objectName] = rows
        }
        answers.__related = related
        log(
          'related layouts: %o',
          relatedLayouts.map(r => r.objectName),
        )
      }

      clearVisibilityCache()

      set({
        layout,
        allFields,
        steps,
        isMultiStep,
        relatedLayouts,
        answers,
        currentStepIndex: 0,
        isLoading: false,
        dataHasChanged: false,
        ...flags,
      })

      // Start auto-save timer if enabled
      if (flags.autoSave) {
        get().stopAutoSave()
        const timer = setInterval(() => {
          const state = get()
          if (state.dataHasChanged && !state.isSaving && !state.isSubmitting && !state.isComplete) {
            log('auto-save triggered')
            state.saveProgress()
          }
        }, AUTO_SAVE_INTERVAL_MS)
        set({ _autoSaveTimer: timer })
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load form'
      log('load failed: %s', message)
      set({ isLoading: false, loadError: message })
    }
  },

  updateAnswer(fieldName: string, value: unknown) {
    const { answers, errors } = get()
    const newAnswers = { ...answers, [fieldName]: value }
    const newErrors = { ...errors }
    delete newErrors[fieldName]
    set({ answers: newAnswers, errors: newErrors, dataHasChanged: true })
  },

  bulkUpdateAnswers(values: Record<string, unknown>) {
    const { answers } = get()
    set({ answers: { ...answers, ...values }, dataHasChanged: true })
  },

  markFieldTouched(fieldName: string) {
    const { touchedFields } = get()
    set({ touchedFields: { ...touchedFields, [fieldName]: true } })

    const state = get()
    const field = state.allFields.find(f => f.name === fieldName)
    if (field) {
      const resolved = state.resolveField(field)
      // Skip validation for hidden fields
      if (!isFieldVisible(resolved, state.answers, state.allFields)) return

      const fieldErrors = validateFieldValue(resolved, state.answers[fieldName])
      if (fieldErrors.length > 0) {
        set({ errors: { ...state.errors, [fieldName]: fieldErrors } })
      }
    }
  },

  validateField(fieldName: string): string[] {
    const { allFields, answers } = get()
    const field = allFields.find(f => f.name === fieldName)
    if (!field) return []
    const resolved = get().resolveField(field)
    return validateFieldValue(resolved, answers[fieldName])
  },

  validateCurrentStep(): boolean {
    const state = get()
    const step = state.getVisibleSteps()[state.currentStepIndex]
    if (!step) return true

    const visibleFields = getVisibleFields(step.fields, state.answers, state.allFields)
    const newErrors: Record<string, string[]> = { ...state.errors }
    let valid = true

    for (const field of visibleFields) {
      const resolved = state.resolveField(field)
      const fieldErrors = validateFieldValue(resolved, state.answers[field.name])
      if (fieldErrors.length > 0) {
        newErrors[field.name] = fieldErrors
        valid = false
      } else {
        delete newErrors[field.name]
      }
    }

    // Validate policy checkboxes for current step
    for (const policy of state.policyBlocks) {
      if (!state.answers[`__policy_${policy.key}`]) {
        newErrors[`__policy_${policy.key}`] = ['You must agree to continue']
        valid = false
      }
    }

    set({ errors: newErrors })
    return valid
  },

  validateAll(): boolean {
    const { allFields, answers } = get()
    const newErrors: Record<string, string[]> = {}
    let valid = true

    const visibleFields = allFields.filter(f => isFieldVisible(f, answers, allFields))

    for (const field of visibleFields) {
      const resolved = get().resolveField(field)
      const fieldErrors = validateFieldValue(resolved, answers[field.name])
      if (fieldErrors.length > 0) {
        newErrors[field.name] = fieldErrors
        valid = false
      }
    }

    set({ errors: newErrors })
    return valid
  },

  goToStep(index: number) {
    const visibleSteps = get().getVisibleSteps()
    if (index >= 0 && index < visibleSteps.length) {
      set({ currentStepIndex: index })
    }
  },

  goToNextStep() {
    const state = get()
    const visibleSteps = state.getVisibleSteps()
    if (state.currentStepIndex < visibleSteps.length - 1) {
      set({ currentStepIndex: state.currentStepIndex + 1 })
    }
  },

  goToPreviousStep() {
    const { currentStepIndex } = get()
    if (currentStepIndex > 0) {
      set({ currentStepIndex: currentStepIndex - 1 })
    }
  },

  async submitForm(done = false): Promise<boolean> {
    const { form, layoutName, instanceId, answers, allFields } = get()
    if (!form || !layoutName) return false

    set({ isSubmitting: true, saveError: null })
    try {
      // Build field type map - only include fields in the layout (layout-level toWire)
      const fieldTypes: Record<string, { type: string; originalType: string }> = {}
      const layoutFieldNames = new Set<string>()
      for (const field of allFields) {
        const resolved = get().resolveField(field)
        fieldTypes[field.name] = { type: resolved.type, originalType: resolved.originalType }
        layoutFieldNames.add(field.name)
      }

      // Only send answers for fields that exist in the layout
      const layoutAnswers: Record<string, unknown> = {}
      for (const key of Object.keys(answers)) {
        if (layoutFieldNames.has(key) || key.startsWith('__')) {
          layoutAnswers[key] = answers[key]
        }
      }

      // Add __ref to new related rows that don't have an Id yet
      const related = layoutAnswers.__related as Record<string, Record<string, unknown>[]> | undefined
      if (related) {
        for (const rows of Object.values(related)) {
          for (const row of rows) {
            if (!row.__ref && !row.Id) {
              row.__ref = generateRef()
            }
          }
        }
      }

      const transformed = transformAnswersForSubmission(layoutAnswers, fieldTypes)
      const tracking = getTrackingData()
      const payload = { ...transformed, ...tracking, done }

      const path = instanceId ? `/data/${form}/${layoutName}/${instanceId}` : `/data/${form}/${layoutName}`

      log('submitting form: %s (done=%s, fields=%d)', path, done, layoutFieldNames.size)
      const { data } = await apiClient.post<DataSubmissionResult>(path, payload)

      if (data?.dataStatus === 'declined') {
        set({
          isDeclined: true,
          isSubmitting: false,
          declineReasons: data.reasons || [],
          dataHasChanged: false,
        })
        get().tryRedirect('declined', data.redirect)
        return false
      }

      if (done) {
        get().stopAutoSave()
        set({ isComplete: true, isSubmitting: false, dataHasChanged: false })
        get().tryRedirect(data?.dataStatus || 'accepted', data?.redirect)
      } else {
        // Apply refMap: server returns mapping of __ref -> real Id
        const currentAnswers = get().answers
        if (data?.refMap && currentAnswers.__related) {
          const relatedData = { ...(currentAnswers.__related as Record<string, Record<string, unknown>[]>) }
          for (const rows of Object.values(relatedData)) {
            for (const row of rows) {
              const ref = row.__ref as string | undefined
              if (ref && !row.Id && data.refMap[ref]) {
                row.Id = data.refMap[ref]
                delete row.__ref
              }
            }
          }
          set({
            isSubmitting: false,
            instanceId: data?.id || instanceId,
            answers: { ...currentAnswers, __related: relatedData, __deleted: undefined },
            dataHasChanged: false,
          })
        } else {
          set({
            isSubmitting: false,
            instanceId: data?.id || instanceId,
            dataHasChanged: false,
          })
        }
      }

      return true
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to submit form'
      log('submit failed: %s', message)
      set({ isSubmitting: false, saveError: message })
      return false
    }
  },

  async saveProgress(): Promise<boolean> {
    set({ isSaving: true })
    const result = await get().submitForm(false)
    set({ isSaving: false })
    return result
  },

  tryRedirect(status: string, serverRedirectUrl?: string): boolean {
    // Priority 1: Server-provided redirect URL
    if (serverRedirectUrl) {
      log('redirect: server provided %s', serverRedirectUrl)
      window.location.href = serverRedirectUrl
      return true
    }
    // Priority 2: Layout redirect section with exact action match
    const { redirects } = get()
    for (const r of redirects) {
      if (r.action === status) {
        log('redirect: layout action=%s -> %s', status, r.url)
        window.location.href = r.url
        return true
      }
    }
    // Priority 3: Catch-all redirect (action === 'all')
    const catchAll = redirects.find(r => r.action === 'all')
    if (catchAll) {
      log('redirect: layout catch-all -> %s', catchAll.url)
      window.location.href = catchAll.url
      return true
    }
    return false
  },

  stopAutoSave() {
    const { _autoSaveTimer } = get()
    if (_autoSaveTimer) {
      clearInterval(_autoSaveTimer)
      set({ _autoSaveTimer: null })
    }
  },

  reset() {
    get().stopAutoSave()
    clearVisibilityCache()
    set(initialState)
  },

  // ─── Computed helpers ──────────────────────────────────────────────────

  resolveField(field: FormLayoutField): FormLayoutField {
    return resolveFieldWithRecordType(field, get().recordTypeId)
  },

  getVisibleSteps(): FormStep[] {
    const { steps, answers, allFields } = get()
    return steps.filter(step => isSectionVisible(step.fields, answers, allFields))
  },

  getCurrentStep(): FormStep | null {
    const state = get()
    const visible = state.getVisibleSteps()
    return visible[state.currentStepIndex] || null
  },

  getVisibleFieldsForStep(stepIndex: number): FormLayoutField[] {
    const state = get()
    const visible = state.getVisibleSteps()
    const step = visible[stepIndex]
    if (!step) return []
    return getVisibleFields(step.fields, state.answers, state.allFields)
  },

  getProgress(): { current: number; total: number; percent: number } {
    const state = get()
    const visible = state.getVisibleSteps()
    const total = visible.length
    const current = Math.min(state.currentStepIndex + 1, total)
    const percent = total > 0 ? Math.round((current / total) * 100) : 0
    return { current, total, percent }
  },

  // ─── Related layout helpers ──────────────────────────────────────────

  resolveRelatedInstanceCount(value: number | string): number {
    return resolveInstanceCount(value, get().answers)
  },

  getRelatedData(objectName: string): Record<string, unknown>[] {
    const related = get().answers.__related as Record<string, Record<string, unknown>[]> | undefined
    return related?.[objectName] || []
  },

  addRelatedRow(objectName: string) {
    const { answers, relatedLayouts } = get()
    const config = relatedLayouts.find(r => r.objectName === objectName)
    if (!config) return

    const related = { ...((answers.__related as Record<string, Record<string, unknown>[]>) || {}) }
    const rows = [...(related[objectName] || [])]

    // Check max instance count
    const maxCount = resolveInstanceCount(config.maxInstanceCount, answers)
    if (maxCount > 0 && rows.length >= maxCount) return

    rows.push({})
    related[objectName] = rows
    set({
      answers: { ...answers, __related: related },
      dataHasChanged: true,
    })
  },

  removeRelatedRow(objectName: string, index: number) {
    const { answers } = get()
    const related = { ...((answers.__related as Record<string, Record<string, unknown>[]>) || {}) }
    const rows = [...(related[objectName] || [])]

    if (index < 0 || index >= rows.length) return

    const [removed] = rows.splice(index, 1)

    // Track deleted rows that have server-side IDs
    const removedId = removed?.Id as string | undefined
    if (removedId) {
      const deleted = { ...((answers.__deleted as Record<string, string[]>) || {}) }
      deleted[objectName] = [...(deleted[objectName] || []), removedId]
      related[objectName] = rows
      set({
        answers: { ...answers, __related: related, __deleted: deleted },
        dataHasChanged: true,
      })
    } else {
      related[objectName] = rows
      set({
        answers: { ...answers, __related: related },
        dataHasChanged: true,
      })
    }
  },

  updateRelatedAnswer(objectName: string, rowIndex: number, fieldName: string, value: unknown) {
    const { answers } = get()
    const related = { ...((answers.__related as Record<string, Record<string, unknown>[]>) || {}) }
    const rows = [...(related[objectName] || [])]

    if (rowIndex < 0 || rowIndex >= rows.length) return

    rows[rowIndex] = { ...rows[rowIndex], [fieldName]: value }
    related[objectName] = rows
    set({
      answers: { ...answers, __related: related },
      dataHasChanged: true,
    })
  },
}))
